import { isEmpty, isEqual } from 'lodash';
import React, { useContext, useEffect, useState } from 'react';
import LoadingCircle from '@cpg-its/ads.common.loading-circle';
import { FormikProps } from 'formik';
import { useDispatch, useSelector } from 'react-redux';
import { BaseApiState } from '../models/api-model';
import { GlobalErrorMessage } from './global-error-message';
import { FormikErrorSummary } from './form-element/formik-error-summary';
import { FormButtons } from './form-element/form-buttons';
import { FormSuccessMessage } from './form-element/form-success-message';
import { FormTypes } from '../../pages/account-info/models/form-models';
import { DocumentUpload } from '../../pages/account-info/features/personal-info/document-upload';
import { TcsmStates } from '../../root/tcsm-model';
import { PersonSearchContext, PersonSearchKeys } from '../../pages/arc/features/person-search/person-search-model';
import { setHideToast, setSearchResults } from '../../pages/arc/features/person-search/person-search-slice';
import { usePendingRequests } from '../hooks/usePendingRequests';
import { useFormCleanup } from '../hooks/useFormCleanup';
import { OSCRRService } from '../../pages/arc/features/person-search/oscrr-helpers';
import { LabelSettingProps } from '../models/epi-model';

interface FormWrapperProps {
	formikProps: FormikProps<any>;
	submitText: string;
	cancelText: string;
	onSubmit?: () => any;
	onCancel: () => any;
	successfulForms?: FormTypes[];
	submitDisabled?: boolean;
	personSearchToast?: JSX.Element;
	formType?: FormTypes;
	defaultShowDocUpload?: boolean;
	hideDocUpload?: boolean;
	hideButtons?: boolean;
	altCancelButton?: boolean;
	hideCancelButton?: boolean;
	requiredDocUpload?: boolean;
	helperText?: string;
	generalErrorMessage?: string;
	formKeys?: string[];
	personSearchResults?: JSX.Element;
	preventButtonDisableValidation?: boolean;
	initialValue?: any;
	onlyContent?: boolean;
	additionalContentAboveButtons?: JSX.Element;
	buttonFlexEnd?: boolean;
}
export function FormWrapper(props: React.PropsWithChildren<FormWrapperProps>) {
    const apiState = useSelector<TcsmStates, BaseApiState>(state => state.apiState);
    const labelSettings = useSelector<TcsmStates, LabelSettingProps>(a => a.epiProps?.labelSettingState?.epiContent);
    const personSearchKey = useContext(PersonSearchContext);
    const pendingRequests = usePendingRequests();
    const dispatch = useDispatch();

	const values = props.formikProps?.values;
	const initialValues = props.initialValue || props.formikProps?.initialValues;
	const [hasNoChanges, setHasNoChanges] = useState(false);

	useEffect(() => {
		const calcNoChanges = values && initialValues ? isEqual(initialValues, values) : false;
		setHasNoChanges(props.preventButtonDisableValidation ? false : calcNoChanges);
	}, [initialValues, values, props.preventButtonDisableValidation]);

	useFormCleanup();

	const resetPersonSearch = () => {
		if (personSearchKey !== PersonSearchKeys.None) {
			dispatch(setSearchResults([], personSearchKey));
			dispatch(setHideToast(false, personSearchKey));
		}
	};

	// clear person search results when form mounts
	useEffect(() => {
		resetPersonSearch();
	}, []);

	if (pendingRequests) {
		return <LoadingCircle />;
	}

	const hasFormikErrors = !isEmpty(props.formikProps.errors);

	const onCancel = () => {
		resetPersonSearch();
		props.onCancel();
	};

	const onSubmit = () => {
		window.scroll({ top: 0, left: 0, behavior: 'smooth' });

		if (props.onSubmit) {
			props.onSubmit();
		}
	};

	if (props.onlyContent) {
		return (
			<>
				{props.children}
			</>
		);
	}

	return (
		<>
			<GlobalErrorMessage generalErrorMessage={hasFormikErrors ? '' : props.generalErrorMessage} />
			<FormikErrorSummary formikProps={props.formikProps} formKeys={props.formKeys} onCancel={props.onCancel} />
			<FormSuccessMessage types={props.successfulForms} />
			{
				props.children
			}
			<DocumentUpload requiredDocUpload={props.requiredDocUpload} formType={props.formType} defaultShow={props.defaultShowDocUpload} hide={props.hideDocUpload} />
			{props.personSearchToast}
			{props.personSearchResults}
			{props.additionalContentAboveButtons}
			{(OSCRRService.IsOSCRRWorking(apiState) ? <LoadingCircle /> : null)}
			{
				!props.hideButtons &&
				<FormButtons
					disabled={apiState.pendingRequests?.length > 0}
					submitText={(OSCRRService.IsOSCRRWorking(apiState) ? labelSettings?.loadingText : props.submitText)} 
					cancelText={props.cancelText}
					altCancelButton={props.altCancelButton}
					onSubmit={onSubmit}
					onCancel={onCancel}
					submitDisabled={props.submitDisabled || hasNoChanges}
					helperText={props.helperText}
					hideCancelButton={props.hideCancelButton}
					flexEnd={props.buttonFlexEnd}
				/>
			}
		</>
	);
}

FormWrapper.defaultProps = {
	successfulForms: null,
	submitDisabled: false,
	personSearchToast: null,
	formType: null,
	defaultShowDocUpload: false,
	hideDocUpload: false,
	hideButtons: false,
	hideCancelButton: false,
	altCancelButton: false,
	requiredDocUpload: false,
	helperText: null,
	generalErrorMessage: null,
	formKeys: null,
	personSearchResults: null,
	preventButtonDisableValidation: false,
	initialValue: null,
	onlyContent: false,
	additionalContentAboveButtons: null,
	onSubmit: null,
	buttonFlexEnd: true,
};